import { Component, OnChanges, OnDestroy, OnInit, SimpleChanges, computed, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import moment from 'moment';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { ParsedBillData, ChargeGroup } from '../../../models';
import { resolveGasUnitId } from '../../../services/bill-persistence.service';
import { ChargeGroupRowComponent } from '../charge-group-row/charge-group-row.component';
import { ScoreSelectComponent, ScoreSelectOption } from '../../bill-ai-flow/score-select/score-select.component';
import { buildPlanSubtitle } from '../../../utils/plan-format.util';
import { ScoreClass, getScoreClass } from '../../../utils/match-score.util';
import { UnitService as UnitV4Service } from '../../../../zentility/_shared/_services/v4/unit-service';
import { UnitDto } from '../../../../zentility/_shared/_model/unit-model';
import { SupplierService } from '../../../../zentility/_shared/_zen-legacy-common/zen-common-services/tili-services/services/supplier.service';
import { Supplier } from '../../../../zentility/_shared/_zen-legacy-common/_models/service-address-settings';
import { ContractsV4Service } from '../../../../zentility/_shared/_services/v4/contracts-v4.service';
import { NearestRankedEnergyPlanModel, ZenContractV4ListModel, ZenContractDialogDataModel, CustomerPlanFinderEntry } from '../../../../zentility/_shared/_model/contract-v4.model';
import { CommodityType } from '../../../../zentility/_shared/_zen-legacy-common/_models/commodity';
import { ZenEditContractDialogComponent } from '../../../../zentility/_shared/_dialogs/zen-edit-contract-dialog/zen-edit-contract-dialog.component';
import { ButtonTypes, ZenDialogDataModel, ZenDialogDataType } from '../../../../zentility/_shared/_dialogs/zen-dialog/zen-dialog.component';
import { ZenDialogSizeEnum, ZenDialogPanelClassEnum } from '../../../../zentility/_shared/_enums/zen-dialogs.enum';
import { ZenDialogMsgService } from '../../../../zentility/_shared/_services/zen-dialog-msg.service';
import { BillingMethod } from '../../../../zentility/_shared/_model/product-configuration.model';
import { buildTotalIssueMessaging, TotalIssueMessaging } from '../../bill-ai-flow/bill-ai-flow-bill-review-step/bill-ai-flow-total-issue.util';
import { computeIntervalTotals, hasIntervalData, resolveIntervalExpanded } from '../../bill-ai-flow/bill-ai-flow-bill-review-step/bill-ai-flow-interval.util';
import { OrganizationManagementService } from '../../../../zentility/_shared/_zen-legacy-common/zen-common-services/_services/organization-management.service';
import {ZenUnitsEnum} from '../../../../zentility/_shared/_enums/zen-units.enum';

/** Cent-level tolerance used when comparing the entered total to Supply + Distribution. */
const TOTAL_AMOUNT_TOLERANCE = 0.01;

/** Billing-period datepicker formats — typed input accepts the common US/ISO shapes. */
export const BILL_DATE_FORMATS = {
  parse: {
    dateInput: ['MM/DD/YYYY', 'M/D/YYYY', 'YYYY-MM-DD'],
  },
  display: {
    dateInput: 'MM/DD/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

/**
 * Minimal context the bill form needs to look up energy plans and pre-fill
 * the create-plan dialog. The host computes this from whatever source it has
 * (the wizard derives it from the BillAiFlowStore); all fields are optional —
 * the form degrades gracefully when entities are new/unknown.
 */
export interface BillFormMeterContext {
  customerId?: number;
  serviceAddressId?: number;
  meterId?: number;
  state?: string;
  commodityType?: CommodityType;
  /** The utility's native unit, used to seed the unit on a new bill. */
  defaultUtilityUnitId?: number;
  /** Key used to index the customer-level plan-finder request/response. */
  planFinderKey?: string;
}

/** A budgeting-mode charge-breakdown row: its display label and the bill field it edits. */
interface ChargeRow {
  label: string;
  field: keyof ParsedBillData;
}

const ELEC_SUPPLY_ROWS: ChargeRow[] = [
  { label: 'Contract Charges', field: 'supplyContractCharges' },
  { label: 'Energy Charges', field: 'supplyEnergyCharges' },
  { label: 'Capacity Charges', field: 'supplyCapacityCharges' },
  { label: 'Transmission Charges', field: 'supplyTransmissionCharges' },
  { label: 'Market Charges', field: 'supplyMarketCharges' },
  { label: 'Line Loss Charges', field: 'supplyLineLossCharges' },
  { label: 'GRT & SUT', field: 'supplyGrtSut' },
  { label: 'Misc Supply Charges', field: 'supplyMiscCharges' },
];

const GAS_SUPPLY_ROWS: ChargeRow[] = [
  { label: 'Contract Charges', field: 'supplyContractCharges' },
  { label: 'Customer Charges', field: 'supplyCustomerCharges' },
  { label: 'Gas Charges', field: 'supplyGasCharges' },
  { label: 'Off Peak Charges', field: 'supplyOffPeakCharges' },
  { label: 'Rate Class Charge', field: 'supplyRateClassCharges' },
  { label: 'Misc Supply Charges', field: 'supplyMiscCharges' },
  { label: 'GRT & SUT', field: 'supplyGrtSut' },
];

const ELEC_DISTRIBUTION_ROWS: ChargeRow[] = [
  { label: 'Customer Charges', field: 'distributionCustomerCharges' },
  { label: 'Energy/Delivery Charges', field: 'distributionEnergyCharges' },
  { label: 'Demand Charges', field: 'distributionDemandCharges' },
  { label: 'Misc Dist. Charges', field: 'distributionMiscCharges' },
];

const GAS_DISTRIBUTION_ROWS: ChargeRow[] = [
  { label: 'Customer Charges', field: 'distributionCustomerCharges' },
  { label: 'Gas Charges', field: 'distributionGasCharges' },
  { label: 'Delivery Charges', field: 'distributionDeliveryCharges' },
  { label: 'Off Peak Charges', field: 'distributionOffPeakCharges' },
  { label: 'Balancing Charges', field: 'distributionBalancingCharges' },
  { label: 'Rate Class Charge', field: 'distributionRateClassCharges' },
  { label: 'Misc Dist. Charges', field: 'distributionMiscCharges' },
  { label: 'GRT & SUT', field: 'distributionGrtSut' },
];

/** Blank bill data used when the host has no parsed data for a bill. */
export function createEmptyParsedBillData(): ParsedBillData {
  return {
    providerName: '', utilityName: '', legalEntity: '',
    utilityAccountNumber: '', utilityAccountNumber2: '', utilityAccountNumber3: '',
    additionalIdentifiers: [], billType: 'unknown',
    serviceAddressStreet1: '', serviceAddressStreet2: '',
    serviceAddressCity: '', serviceAddressState: '', serviceAddressZip: '',
    billingPeriodStart: '', billingPeriodEnd: '',
    totalAmount: 0, currency: 'USD',
    supplyCharges: 0, supplyTaxes: 0, supplyTotalCost: 0,
    distributionCharges: 0, distributionTaxes: 0, distributionTotalCost: 0,
    usageAmount: 0, usageUnit: '', confidence: 0, verifiedFields: []
  };
}

/**
 * Pure, reusable bill form — billing period, usage, demand, supplier/energy
 * plan, interval usages, and the Financials section (basic + budgeting mode).
 *
 * Extracted from BillAiFlowBillReviewStepComponent; the wizard step now hosts
 * this form and keeps all store/navigation/merge/validation orchestration.
 * The form deep-copies `billData` into internal state and re-initializes
 * whenever the input reference changes (the host passes a fresh clone per
 * bill). Edits are surfaced through `formChanged`; the host decides when to
 * persist. `commitRequested` fires when the form performs an action that the
 * host should immediately persist (undo / revert), mirroring the original
 * in-step save behavior.
 */
@Component({
  selector: 'app-bill-form',
  standalone: true,
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatTooltipModule, MatSlideToggleModule, MatDialogModule, MatDatepickerModule, ChargeGroupRowComponent, ScoreSelectComponent],
  templateUrl: './bill-form.component.html',
  styleUrl: './bill-form.component.scss',
  providers: [
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: BILL_DATE_FORMATS },
  ],
})
export class BillFormComponent implements OnInit, OnChanges, OnDestroy {
  /** Initial bill data; deep-copied into internal form state on every reference change. */
  readonly billData = input<ParsedBillData | null>(null);
  /** Budgeting (pro) mode switches the Financials section to the charge-group grid. */
  readonly isBudgetingMode = input(false);
  /** Per-field blocking errors for the current bill (drives .field-error styling). */
  readonly fieldErrors = input<Record<string, boolean>>({});
  /** Per-field warnings for the current bill (drives .field-warn styling). */
  readonly fieldWarnings = input<Record<string, boolean>>({});
  /** Entity context for energy-plan lookup / create-plan dialog. */
  readonly meterContext = input<BillFormMeterContext | null>(null);
  /** Gas meters have no demand or interval (TOU) usage — hide those inputs. */
  readonly isGasMeter = computed(() => this.meterContext()?.commodityType === CommodityType.Gas);

  /** Budgeting-mode charge rows per commodity — single source of truth for the
   *  Supply/Distribution breakdown, iterated in the template instead of hand-listed. */
  readonly supplyChargeRows = computed<ChargeRow[]>(() => this.isGasMeter() ? GAS_SUPPLY_ROWS : ELEC_SUPPLY_ROWS);
  readonly distributionChargeRows = computed<ChargeRow[]>(() => this.isGasMeter() ? GAS_DISTRIBUTION_ROWS : ELEC_DISTRIBUTION_ROWS);
  /** Dual billing (separate utility + supplier bills). Gates per-section toggling
   *  in budgeting mode — a consolidated bill always carries both sides. */
  readonly dualBilling = input(false);
  /** Per-section toggling is available in basic mode always, and in budgeting mode
   *  only for dual bills (where one side may be absent). */
  readonly sectionTogglesAvailable = computed(() => !this.isBudgetingMode() || this.dualBilling());

  /** Per-section enable state. Off = that side isn't on this bill: its inputs are
   *  collapsed and it's excluded from validation. Both off = total-only bill. */
  readonly supplyEnabled = signal(true);
  readonly distributionEnabled = signal(true);

  /** Section-toggle tooltip copy, shared by the basic- and budgeting-mode templates. */
  readonly supplyToggleTooltip = "Tracks supply charges on this bill. Turn off to skip them, or if the bill doesn't include any.";
  readonly distributionToggleTooltip = "Tracks distribution charges on this bill. Turn off to skip them, or if the bill doesn't include any.";

  /** Emitted with the live form data after every user-driven mutation. */
  readonly formChanged = output<ParsedBillData>();
  /** Emitted when the dirty flag flips. */
  readonly dirtyChanged = output<boolean>();
  /** Emitted when the form wants the host to persist immediately (undo/revert). */
  readonly commitRequested = output<void>();

  /** Exposed as signals so host templates can bind footer/nav button state. */
  readonly isFormValid = signal(false);
  readonly canUndo = signal(false);

  // Form data (cloned from billData input)
  formData!: ParsedBillData;
  isDirty = false;

  // Undo stack — snapshots of formData before each user edit
  private undoStack: string[] = [];
  private readonly MAX_UNDO = 50;
  private lastEmittedDirty = false;

  // Pre-computed state (updated in updateComputedValues)
  isSupplierSelected = false;
  isEnergyPlanRequired = false;
  isEnergyPlanDisabled = false;

  // Pre-computed charge group map for template (avoids getGroup() calls per CD cycle)
  chargeGroupMap: Record<string, ChargeGroup> = {};

  // Pre-computed formatted original total for revert button title
  formattedOriginalTotal = '';

  // Dropdown data
  availableUnits: UnitDto[] = [];
  /** Unfiltered units from the API — re-filtered per commodity when meterContext changes. */
  private allUnits: UnitDto[] = [];
  suppliers: Supplier[] = [];
  supplierOptions: ScoreSelectOption[] = [];
  supplierSelectValue: any = null;

  // Energy plan dropdown
  energyPlanOptions: ScoreSelectOption[] = [];
  energyPlansLoading = false;
  canCreatePlan = false;
  private energyPlanIndex = new Map<number, NearestRankedEnergyPlanModel>();
  private allEnergyPlans: NearestRankedEnergyPlanModel[] = [];
  /** Params of the last successful plan fetch — an identical request re-filters in memory instead of refetching. */
  private lastPlanFetchSignature: string | null = null;
  /** Monotonic fetch counter so a stale (slower) response can't clobber a newer one. */
  private planFetchId = 0;

  // Pre-computed template values
  formattedDemand = '';
  formattedUsageAmount = '';

  // Billing-period values as moments — model for the datepicker inputs
  billingPeriodStartValue: moment.Moment | null = null;
  billingPeriodEndValue: moment.Moment | null = null;

  // Interval usage pre-computed values
  formattedOnPeakUsage = '';
  formattedOnPeakCost = '';
  formattedOnPeakRate = '—';
  formattedIntPeakUsage = '';
  formattedIntPeakCost = '';
  formattedIntPeakRate = '—';
  formattedOffPeakUsage = '';
  formattedOffPeakCost = '';
  formattedOffPeakRate = '—';
  formattedIntervalTotalUsage = '';
  formattedIntervalTotalCost = '';
  formattedIntervalBlendedRate = '—';
  intervalUsageMismatch = false;
  intervalUsageMismatchTooltip = '';
  showIntervalUsages = false;
  hasIntervalData = false;
  confidenceData: Record<string, { percent: string; good: boolean; bad: boolean; ugly: boolean }> = {};
  /** True only when the bill data came with real AI per-field confidence — gates all confidence UI. */
  hasAiConfidence = false;
  formattedTotalAmount = '';
  formattedSupplyCharges = '';
  formattedSupplyTax = '';
  formattedTotalSupply = '';
  formattedDistCharges = '';
  formattedDistTax = '';
  formattedTotalDist = '';
  formattedSupplyEffectiveRate = '—';
  formattedDistEffectiveRate = '—';
  /** "charges ÷ usage unit =" shown beside each effective rate; empty when either side is missing. */
  formattedSupplyRateFormula = '';
  formattedDistRateFormula = '';
  /**
   * Whether the budgeting form shows the Supply Sales Tax group. Gas bills carry
   * sales tax on both sides; electricity bills only on distribution, so an
   * electricity bill shows the supply group only when it actually has a value.
   */
  showSupplySalesTax = true;
  formattedTotalEffectiveRate = '—';
  totalMismatch = false;
  totalManuallyChanged = false;
  /** True when the bill never had a real total (manual create, or a persisted/AI
   *  bill with no total) — the total auto-tracks the enabled sections until the
   *  user edits the total field. */
  private autoTotalEligible = false;
  /** Signed diff applied by the supply/distribution quick-fix buttons; 0 when no mismatch. */
  mismatchQuickFixDiff = 0;
  totalIssue: TotalIssueMessaging = {
    hasIssue: false, iconGlyph: '', tooltip: '', explanation: '',
    supplyActionLabel: '', supplyActionAmount: '',
    distributionActionLabel: '', distributionActionAmount: '',
    recalculateActionLabel: '', recalculateActionAmount: '',
  };
  private totalAmountValue = 0;
  private calculatedTotalValue = 0;
  private totalDiffValue = 0;
  originalBillData: ParsedBillData | null = null;

  /** Field currently being edited — its formatted value is NOT overwritten by updateComputedValues() */
  private activeEditField: string | null = null;
  /** Snapshot pushed on first keystroke; cleared on blur to avoid per-keystroke undo entries */
  private pendingUndoForField: string | null = null;

  private unitV4Service = inject(UnitV4Service);
  private supplierService = inject(SupplierService);
  private contractsV4Service = inject(ContractsV4Service);
  private dialog = inject(MatDialog);
  private zenDialogSvc = inject(ZenDialogMsgService);
  private orgMgtSvc = inject(OrganizationManagementService);

  /** Org-level budgeting entitlement — the "upgrade" upsell only makes sense when off. */
  orgHasBudgetingFeature = false;

  /** Completes on destroy to tear down long-lived subscriptions. */
  private readonly destroy$ = new Subject<void>();
  /** Guards deferred (microtask) emits against running after teardown. */
  private destroyed = false;

  ngOnInit(): void {
    // Org budgeting entitlement gates the upsell. Read from organizationSubject —
    // the bare `.organization` is documented as unreliable (not always set), which
    // would wrongly upsell entitled orgs that just have budgeting toggled off.
    this.orgMgtSvc.organizationSubject
      .pipe(takeUntil(this.destroy$))
      .subscribe(org => this.orgHasBudgetingFeature = !!org?.settings?.featureBudgeting);
    // ngOnChanges only fires when at least one input is bound — make sure a
    // host that binds nothing still gets an initialized (empty) form.
    if (!this.formData) {
      this.initializeForm(this.billData());
    }
    this.loadDropdownData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['billData']) {
      this.initializeForm(this.billData());
      // The next bill can be a different commodity (dual-fuel PDF), so re-scope units too.
      if (changes['meterContext']) this.filterAvailableUnits();
      return;
    }
    if (changes['meterContext']) {
      // Re-derive plan availability and commodity-scoped units when the host
      // recomputes entity context.
      this.loadEnergyPlans();
      this.filterAvailableUnits();
    }
    if (changes['fieldErrors']) {
      // Keep the total-issue icon in sync with the host's validation pass.
      this.refreshTotalIssue();
    }
    if ((changes['isBudgetingMode'] || changes['dualBilling']) && this.formData) {
      // Entering budgeting mode on a consolidated bill (or leaving dual) forces
      // both sides on — only dual bills may carry a single side in budgeting mode.
      this.enforceSectionAvailability();
      this.syncSectionFlags();
      // Surface the synced flags so the host's copy stays in step. Deferred a
      // microtask: this runs inside the parent's change-detection pass, so a
      // synchronous emit would trip ExpressionChangedAfterItHasBeenCheckedError.
      Promise.resolve().then(() => {
        if (!this.destroyed) this.emitChange();
      });
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ===========================================================================
  // Public API (used by hosts via template ref / viewChild)
  // ===========================================================================

  /** Returns the live internal form data (host clones before persisting). */
  getFormData(): ParsedBillData {
    return this.formData;
  }

  /** Re-initializes the form from the given data (same as a billData input swap). */
  resetTo(data: ParsedBillData | null): void {
    this.initializeForm(data);
  }

  /**
   * Replaces the form contents with externally merged data (dual-bill flow)
   * WITHOUT resetting the undo stack, mirroring the original in-step merge.
   */
  applyMergedData(data: ParsedBillData): void {
    this.formData = data;
    // Externally merged data carrying a real total (dual-bill extraction) ends
    // auto-tracking — that total must be reconciled, not overwritten.
    if (data.totalAmount) {
      this.autoTotalEligible = false;
    }
    this.hasAiConfidence = this.hasAiConfidence
      || (!!data?.fieldConfidence && Object.keys(data.fieldConfidence).length > 0);
    if (this.hasAiConfidence) {
      this.ensureConfidenceDefaults();
    }
    this.isDirty = true;
    this.updateComputedValues();
    this.buildSupplierOptions();
    this.loadEnergyPlans();
    this.emitChange();
  }

  // ===========================================================================
  // Initialization
  // ===========================================================================

  private initializeForm(data: ParsedBillData | null): void {
    // Confidence dots only make sense for AI-extracted data — a manually
    // entered or persisted bill has no real per-field confidence, so defaults
    // would just render misleading dots.
    this.hasAiConfidence = !!data?.fieldConfidence && Object.keys(data.fieldConfidence).length > 0;
    if (data) {
      this.originalBillData = JSON.parse(JSON.stringify(data));
      this.formData = JSON.parse(JSON.stringify(data));
      if (this.hasAiConfidence) {
        this.ensureConfidenceDefaults();
      }
    } else {
      this.formData = createEmptyParsedBillData();
      // A fresh empty form has no revert baseline — drop the previous bill's.
      this.originalBillData = null;
    }

    // Per-section enable state. An existing bill loads each section on only when
    // it carries values, so a dual utility bill (distribution only) opens with
    // Supply off and a dual supplier bill opens with Distribution off; a fresh
    // empty bill opens with both on; a total-only bill (a billing total but no
    // breakdown) opens with both off. Explicit persisted flags win.
    // A legacy section total with no charge breakdown counts as values — the
    // side opens on showing that total instead of being switched off.
    const hasSupply = (this.formData.supplyCharges || 0) > 0 || (this.formData.supplyTaxes || 0) > 0
      || (this.formData.supplyTotalCost || 0) > 0;
    // Distribution may be a negative credit (DEV-15235) — any nonzero value
    // counts so a credit bill reopens with the section on.
    const hasDist = (this.formData.distributionCharges || 0) !== 0 || (this.formData.distributionTaxes || 0) !== 0
      || (this.formData.distributionTotalCost || 0) !== 0;
    // Nonzero — a total-only credit bill has a negative total (DEV-15235) and
    // must still open with both sections off, showing just that total.
    const hasTotal = (this.formData.totalAmount ?? 0) !== 0;
    let supplyOn: boolean;
    let distOn: boolean;
    if (hasSupply || hasDist) {
      supplyOn = hasSupply;
      distOn = hasDist;
    } else {
      supplyOn = distOn = !hasTotal;
    }
    if (this.formData.financialsDisabled === true) supplyOn = distOn = false;
    if (this.formData.supplyDisabled != null) supplyOn = !this.formData.supplyDisabled;
    if (this.formData.distributionDisabled != null) distOn = !this.formData.distributionDisabled;
    this.supplyEnabled.set(supplyOn);
    this.distributionEnabled.set(distOn);
    this.enforceSectionAvailability();
    this.syncSectionFlags();

    this.isDirty = false;
    this.undoStack = [];
    this.totalManuallyChanged = false;
    // Null/0 counts as "never had a total"; a negative total is a real credit
    // bill and must not be auto-overwritten.
    this.autoTotalEligible = !this.formData.totalAmount;
    if (this.lastEmittedDirty) {
      this.lastEmittedDirty = false;
      this.dirtyChanged.emit(false);
    }
    this.updateComputedValues();
    this.buildSupplierOptions();
    this.loadEnergyPlans();
  }

  private emitChange(): void {
    this.formChanged.emit(this.formData);
    if (this.isDirty !== this.lastEmittedDirty) {
      this.lastEmittedDirty = this.isDirty;
      this.dirtyChanged.emit(this.isDirty);
    }
  }

  // ===========================================================================
  // Dropdown Data
  // ===========================================================================

  private loadDropdownData(): void {
    this.unitV4Service.getUnits().pipe(takeUntil(this.destroy$)).subscribe({
      next: units => {
        this.allUnits = units || [];
        this.filterAvailableUnits();
      },
      // The global interceptor only surfaces 401/403 — without this the user
      // gets a silently empty Units dropdown and can't finish the bill.
      error: () => this.zenDialogSvc.openToast(false, 'Failed to load units. Please reopen the editor to retry.'),
    });

    this.supplierService.getSuppliers().pipe(takeUntil(this.destroy$)).subscribe({
      next: (result: any) => {
        this.suppliers = result?.suppliers || result || [];
        this.buildSupplierOptions();
      },
      error: () => this.zenDialogSvc.openToast(false, 'Failed to load providers. Please reopen the editor to retry.'),
    });
  }

  /**
   * Units dropdown follows the meter's commodity — gas meters get gas units
   * (therms/CCF/…), not kWh. 'ALL'-commodity units are always included.
   */
  private filterAvailableUnits(): void {
    const commodity = this.meterContext()?.commodityType === CommodityType.Gas
      ? CommodityType.Gas : CommodityType.Electricity;
    this.availableUnits = this.allUnits.filter(u =>
      u.commodityType === commodity || (u.commodityType as string) === 'ALL'
    );
    this.applyDefaultUnitIfUnset(commodity);
  }

  /**
   * New bills open with no unit, so default to the utility's native unit — a gas
   * utility billing in therms must not default to Dth, or 2038 therms is stored as
   * 2038 Dth (10x). Falls back to the commodity's platform unit when the utility has
   * none. Never overrides a unit already on the bill (extracted, persisted, picked).
   */
  private applyDefaultUnitIfUnset(commodity: CommodityType): void {
    if (!this.formData || this.formData.usageUnit) return;
    // Resolve a persisted unit id (with no name) from the units list before using the default.
    if (this.formData.usageUnitId != null) {
      const byId = this.availableUnits.find(u => u.id === this.formData.usageUnitId);
      if (byId) {
        this.formData.usageUnit = byId.unit;
        this.updateComputedValues();
        Promise.resolve().then(() => {
          if (!this.destroyed) this.emitChange();
        });
        return;
      }
    }
    // Match the utility by id, not name — the platform stores 'Th', not 'Therms'.
    const defaultName = commodity === CommodityType.Gas ? ZenUnitsEnum.Dth : ZenUnitsEnum.kWh;
    const utilityUnitId = this.meterContext()?.defaultUtilityUnitId;
    const byUtility = utilityUnitId != null
      ? this.availableUnits.find(u => u.id === utilityUnitId)
      : undefined;
    const match = byUtility ?? this.availableUnits.find(u => u.unit === defaultName);
    if (!match) return;
    this.formData.usageUnit = match.unit;
    this.formData.usageUnitId = match.id;
    this.updateComputedValues();
    // This can run from ngOnChanges (meterContext) inside the parent's change
    // detection — defer the emit a microtask to avoid ExpressionChangedAfterItHasBeenCheckedError.
    Promise.resolve().then(() => {
      if (!this.destroyed) this.emitChange();
    });
  }

  /**
   * Load energy plans for the current meter's billing period.
   * Only loads plans when a supplier is selected (on-utility bills don't need plans).
   * Strategy:
   * 1. If all three resolved IDs exist (customerId + saId + meterId): use meter-level plan-finder API
   * 2. If only customerId exists (new entities): use customer-level batch plan-finder API
   * 3. If no customerId: show empty
   */
  private loadEnergyPlans(): void {
    const ctx = this.meterContext();
    const customerId = this.resolvedCustomerId();
    const saId = ctx?.serviceAddressId;
    const meterId = ctx?.meterId;

    // Update canCreatePlan: need a customerId and a non-utility supplier.
    // Read supplierId off the form directly — isSupplierSelected is only
    // refreshed by updateComputedValues() and can be stale mid-handler.
    this.canCreatePlan = !!customerId && !!this.formData.supplierId;

    // No supplier = on utility — no energy plan needed
    if (!this.formData.supplierId) {
      this.energyPlanOptions = [];
      return;
    }

    const startDate = this.formData.billingPeriodStart;
    const endDate = this.formData.billingPeriodEnd;

    // Path 1: All three resolved IDs — use specific plan-finder
    if (customerId && saId != null && meterId != null) {
      if (!startDate || !endDate) {
        this.energyPlanOptions = [];
        return;
      }
      // The meter-level fetch is supplier-independent — a supplier switch within
      // the same period only needs the in-memory re-filter, not a refetch.
      const signature = `meter|${customerId}|${saId}|${meterId}|${startDate}|${endDate}`;
      if (signature === this.lastPlanFetchSignature) {
        // Invalidate any in-flight request for a different signature — its
        // late response must not clobber the already-correct plans.
        this.planFetchId++;
        this.energyPlansLoading = false;
        this.filterEnergyPlansBySupplier();
        return;
      }
      const fetchId = ++this.planFetchId;
      this.energyPlansLoading = true;
      this.contractsV4Service
        .getContractPlansForMeter(customerId, saId, meterId, startDate, endDate)
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            if (fetchId !== this.planFetchId) return;
            this.energyPlansLoading = false;
            this.lastPlanFetchSignature = signature;
            this.buildEnergyPlanOptions(res?.energyPlans || []);
          },
          error: (err) => {
            if (fetchId !== this.planFetchId) return;
            console.warn('Failed to load energy plans for meter:', err);
            this.energyPlansLoading = false;
            this.energyPlanOptions = [];
          }
        });
      return;
    }

    // Path 2: Have customerId but entities are new — use customer-level plan-finder API
    if (customerId) {
      const supplierId = this.formData.supplierId;

      // No supplier = on utility — no energy plan needed
      if (!supplierId || !startDate || !endDate) {
        this.energyPlanOptions = [];
        return;
      }

      const state = ctx?.state || '';
      const commodityType = ctx?.commodityType || CommodityType.Electricity;

      if (!state) {
        this.energyPlanOptions = [];
        return;
      }

      // Unlike the meter-level fetch, this lookup is supplier-scoped — the
      // signature includes the supplier so a supplier switch refetches.
      const signature = `customer|${customerId}|${supplierId}|${state}|${commodityType}|${startDate}|${endDate}`;
      if (signature === this.lastPlanFetchSignature) {
        // Invalidate any in-flight request for a different signature — its
        // late response must not clobber the already-correct plans.
        this.planFetchId++;
        this.energyPlansLoading = false;
        this.filterEnergyPlansBySupplier();
        return;
      }
      const fetchId = ++this.planFetchId;
      this.energyPlansLoading = true;

      const entry: CustomerPlanFinderEntry = {
        key: ctx?.planFinderKey || 'bill-form',
        supplierId,
        state,
        commodityType,
        billStartDate: startDate,
        billEndDate: endDate,
        meterId: meterId ?? null,
        serviceAddressId: saId ?? null,
      };

      this.contractsV4Service.findEnergyPlansForCustomer(customerId, [entry])
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            if (fetchId !== this.planFetchId) return;
            this.energyPlansLoading = false;
            this.lastPlanFetchSignature = signature;
            const result = res?.results?.[entry.key];
            this.buildEnergyPlanOptions(result?.energyPlans || []);
          },
          error: (err) => {
            if (fetchId !== this.planFetchId) return;
            console.warn('Failed to load energy plans (plan-finder):', err);
            this.energyPlansLoading = false;
            this.energyPlanOptions = [];
          }
        });
      return;
    }

    // Path 3: No customerId — only empty
    this.energyPlanOptions = [];
  }

  /** Resolve the customerId from the host-provided meter context. */
  private resolvedCustomerId(): number | null {
    const id = this.meterContext()?.customerId;
    return id != null && !isNaN(id) ? id : null;
  }

  private buildSupplierOptions(): void {
    const fc = (this.formData?.fieldConfidence as any)?.providerName || 0;
    const confidenceScore = Math.round(fc * 100);
    const scoreClass: ScoreClass = getScoreClass(confidenceScore);
    // Score chip only marks the AI-extracted supplier — not a manually picked one.
    const extractedSupplierId = this.hasAiConfidence ? this.formData?.supplierId : undefined;

    this.supplierOptions = [
      { value: '__on_utility__', label: 'On Utility', dividerAfter: true },
      ...[...this.suppliers]
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        .map(s => ({
          value: s.id,
          label: s.name,
          score: s.id === extractedSupplierId ? confidenceScore : undefined,
          scoreClass: s.id === extractedSupplierId ? scoreClass : undefined
        }))
    ];

    this.supplierSelectValue = this.formData?.supplierId
      || (this.formData?.providerName === 'On Utility' ? '__on_utility__' : null);
  }

  private buildEnergyPlanOptions(plans: NearestRankedEnergyPlanModel[]): void {
    this.allEnergyPlans = [...plans].sort((a, b) => (b.rank ?? 0) - (a.rank ?? 0));
    this.energyPlanIndex.clear();
    for (const p of this.allEnergyPlans) {
      this.energyPlanIndex.set(p.energyPlanId, p);
    }
    this.filterEnergyPlansBySupplier();
    // Filtering/auto-select can mutate energyPlanId after an async load —
    // refresh dependent computed state (e.g. isFormValid) and surface the
    // (possibly unchanged) data to the host.
    this.updateComputedValues();
    this.emitChange();
  }

  private filterEnergyPlansBySupplier(): void {
    const supplierId = this.formData.supplierId;

    // No supplier = on utility — no energy plan needed
    if (!supplierId) {
      this.energyPlanOptions = [];
      this.formData.energyPlanId = undefined;
      return;
    }

    const filtered = this.allEnergyPlans.filter(p => p.supplierId === supplierId);

    const mapped: ScoreSelectOption[] = filtered.map(p => {
      const isDraft = p.planEndDate == null;
      // Show the plan's own rate unit after the rate, e.g. "@ 0.07500 / kWh".
      const unitSuffix = p.unit ? ` / ${p.unit}` : '';
      const rate = p.supplierRate != null ? `${Number(p.supplierRate).toFixed(5)}${unitSuffix}` : '';

      let label: string;
      if (isDraft) {
        label = rate ? `${p.supplierName} @ ${rate} (DRAFT)` : `${p.supplierName} (DRAFT)`;
      } else {
        const term = p.termMonths != null ? `${p.termMonths}` : '?';
        label = rate
          ? `${p.supplierName} for ${term} Mo @ ${rate}`
          : `${p.supplierName} for ${term} Mo`;
      }

      // Use backend rank directly — it already accounts for meter linkage,
      // date overlap, expiration, and plan hierarchy. No rank (e.g. a plan the
      // user just created) means no chip; 0% would be misleading.
      const score = p.rank;

      return {
        value: p.energyPlanId,
        label,
        subtitle: buildPlanSubtitle(p),
        score: score ?? undefined,
        scoreClass: score == null ? undefined : score >= 80 ? 'high' : score > 50 ? 'med' : 'low',
        badge: p.csa ? 'CSA' : undefined
      };
    });

    this.energyPlanOptions = mapped;

    // Clear selection if current plan is no longer in the filtered list
    if (this.formData.energyPlanId && !mapped.some(o => o.value === this.formData.energyPlanId)) {
      this.formData.energyPlanId = undefined;
    }

    // Auto-select best matching plan if none is selected
    if (!this.formData.energyPlanId && filtered.length > 0) {
      this.autoSelectBestPlan(filtered);
    }
  }

  /**
   * Auto-select the best matching energy plan if none is currently selected.
   * Uses backend rank (via plan.rank) — requires rank >= 70 to auto-select.
   */
  private autoSelectBestPlan(plans: NearestRankedEnergyPlanModel[]): void {
    let bestPlan: NearestRankedEnergyPlanModel | null = null;
    let bestRank = 0;

    for (const plan of plans) {
      const rank = plan.rank ?? 0;
      if (rank > bestRank) {
        bestRank = rank;
        bestPlan = plan;
      }
    }

    if (bestPlan && bestRank >= 70) {
      this.formData.energyPlanId = bestPlan.energyPlanId;
      this.isDirty = true;
      this.updateComputedValues();
    }
  }

  handleEnergyPlanChange(planId: number | null): void {
    this.pushUndoSnapshot();
    this.formData.energyPlanId = planId ?? undefined;

    // When a plan is selected, update supplier info from the plan
    if (planId && this.energyPlanIndex.has(planId)) {
      const plan = this.energyPlanIndex.get(planId)!;
      this.formData.supplierId = plan.supplierId;
      this.formData.providerName = plan.supplierName;
    } else if (planId === null) {
      this.formData.supplierId = undefined;
      this.formData.providerName = 'On Utility';
      this.formData.energyPlanId = undefined;
    }
    this.isDirty = true;
    this.updateComputedValues();
    this.emitChange();
  }

  /**
   * Open the contract creation dialog pre-filled with known data from the current bill context.
   */
  openCreatePlanDialog(): void {
    const customerId = this.resolvedCustomerId();
    if (!customerId) return;

    const ctx = this.meterContext();
    const commodityType = ctx?.commodityType === CommodityType.Gas ? CommodityType.Gas : CommodityType.Electricity;

    // Infer billing method from bill charges. Distribution counts when nonzero —
    // a credit (negative, DEV-15235) is still a distribution side on the bill.
    const hasSupply = (this.formData.supplyCharges || 0) > 0;
    const hasDist = (this.formData.distributionCharges || 0) !== 0;
    const billingMethod = (hasSupply && hasDist) ? BillingMethod.consolidated : BillingMethod.dual;

    // Compute approximate per-unit supply rate from bill. A gas rate in an unknown unit would be
    // saved as Dth, so it is only filled in with its unit
    const usage = this.formData.usageAmount || 0;
    const rateUnitId = commodityType === CommodityType.Gas
      ? resolveGasUnitId(this.formData.usageUnitId, this.formData.usageUnit) : undefined;
    const supplyRate = (usage > 0 && hasSupply && (commodityType !== CommodityType.Gas || rateUnitId !== undefined))
      ? parseFloat(((this.formData.supplyCharges || 0) / usage).toFixed(5))
      : undefined;

    const serviceName = commodityType === CommodityType.Electricity ? 'Electricity' : 'Gas';

    let dialogRef;
    let dialogData: ZenDialogDataModel = {
      type: ZenDialogDataType.ADD_DIALOG,
      header: { title: `Add ${serviceName} Contract` },
      bodyText: `Add the required details below to create a new ${serviceName} supply contract.`,
      data: {
        lockedCustomerId: customerId,
        lockedCommodityType: commodityType,
        lockedState: ctx?.state || '',
        lockedSupplierId: this.formData.supplierId || undefined,
        lockedSupplierRate: supplyRate,
        lockedSupplierRateUnitId: rateUnitId,
        lockedBillingMethod: billingMethod
      } as Partial<ZenContractDialogDataModel>,
      onClose: () => dialogRef.close(),
      actionButtons: [
        { label: 'Cancel', command: () => dialogRef.close() },
        {
          label: 'Save', btnType: ButtonTypes.MAT_RAISED_BUTTON, color: 'primary', styleClass: 'ms-5',
          command: () => dialogRef.close()
        }
      ]
    };

    dialogRef = this.dialog.open(ZenEditContractDialogComponent, {
      width: ZenDialogSizeEnum.LARGE,
      panelClass: ZenDialogPanelClassEnum.MOBILE_FULL_HEIGHT,
      data: dialogData,
      autoFocus: false
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: ZenContractV4ListModel) => {
      if (result) {
        // Inject the new plan into the local list so it appears in the dropdown.
        // No rank — a just-created plan has no score, so it gets no chip.
        const newPlan = { ...result, rank: null } as NearestRankedEnergyPlanModel;
        this.allEnergyPlans = [newPlan, ...this.allEnergyPlans];
        this.energyPlanIndex.set(result.energyPlanId, newPlan);

        // Update supplier to match the new plan, then rebuild dropdown options
        this.formData.supplierId = result.supplierId;
        this.formData.providerName = result.supplierName;
        this.filterEnergyPlansBySupplier();

        // Auto-select the newly created plan
        this.formData.energyPlanId = result.energyPlanId;
        this.isDirty = true;
        this.updateComputedValues();
        this.emitChange();
      }
    });
  }

  /** Handle comma-formatted number fields (usage, demand) — parse on each keystroke but defer formatting to blur */
  handleFormattedNumberChange(field: keyof ParsedBillData, displayValue: string): void {
    if (this.pendingUndoForField !== field) {
      this.pushUndoSnapshot();
      this.pendingUndoForField = field;
    }
    this.activeEditField = field;
    const num = parseFloat(displayValue.replace(/[^0-9.\-]/g, ''));
    (this.formData as any)[field] = isNaN(num) ? (field === 'demandKw' ? undefined : 0) : num;
    this.isDirty = true;
    this.updateComputedValues();
    this.emitChange();
  }

  onFormattedNumberBlur(field: keyof ParsedBillData, inputEl?: HTMLInputElement): void {
    this.activeEditField = null;
    this.pendingUndoForField = null;
    this.updateComputedValues();
    this.resyncInputDisplay(field, inputEl);
  }

  /** Maps a formatted numeric/currency field to the property holding its display string. */
  private readonly formattedPropByField: Record<string, string> = {
    usageAmount: 'formattedUsageAmount',
    demandKw: 'formattedDemand',
    onPeakUsage: 'formattedOnPeakUsage',
    intermediateUsage: 'formattedIntPeakUsage',
    offPeakUsage: 'formattedOffPeakUsage',
    totalAmount: 'formattedTotalAmount',
    onPeakCost: 'formattedOnPeakCost',
    intermediateCost: 'formattedIntPeakCost',
    offPeakCost: 'formattedOffPeakCost',
    supplyCharges: 'formattedSupplyCharges',
    supplyTaxes: 'formattedSupplyTax',
    distributionCharges: 'formattedDistCharges',
    distributionTaxes: 'formattedDistTax',
  };

  /**
   * Force a formatted input's DOM text back to its sanitized value. One-way
   * [ngModel] doesn't rewrite the input when the recomputed formatted string is
   * unchanged, so typed invalid characters (e.g. "62,261abc") would otherwise
   * linger after blur even though the model already holds a clean number.
   */
  private resyncInputDisplay(field: keyof ParsedBillData, inputEl?: HTMLInputElement): void {
    if (!inputEl) return;
    const prop = this.formattedPropByField[field as string];
    if (prop) inputEl.value = (this as any)[prop] ?? '';
  }

  // ===========================================================================
  // Form Handlers
  // ===========================================================================

  handleChange(field: keyof ParsedBillData, value: any): void {
    this.pushUndoSnapshot();
    (this.formData as any)[field] = value;
    this.isDirty = true;
    this.updateComputedValues();
    this.emitChange();
  }

  /**
   * Billing-period datepicker model change — fires on a calendar pick, when
   * typed text parses to a valid date, and with an invalid/null moment while
   * typed text doesn't parse. Only valid dates are written to the form;
   * unparseable typing keeps the last valid value and is resolved on blur
   * (empty input = intentional clear, garbage input = revert). While the input
   * is focused the moment property is NOT re-synced (activeEditField), so
   * partial typing isn't clobbered by the model write-back.
   */
  handleDateChange(field: 'billingPeriodStart' | 'billingPeriodEnd', value: moment.Moment | null): void {
    if (!moment.isMoment(value) || !value.isValid()) {
      this.activeEditField = field;
      return;
    }
    if (this.pendingUndoForField !== field) {
      this.pushUndoSnapshot();
      this.pendingUndoForField = field;
    }
    this.activeEditField = field;
    (this.formData as any)[field] = value.format('YYYY-MM-DD');
    this.isDirty = true;
    // A period change invalidates the plan lookup (no-op until both dates are set).
    this.loadEnergyPlans();
    this.updateComputedValues();
    this.emitChange();
  }

  onDateBlur(field: 'billingPeriodStart' | 'billingPeriodEnd', inputText: string): void {
    this.activeEditField = null;
    this.pendingUndoForField = null;
    const prop = field === 'billingPeriodStart' ? 'billingPeriodStartValue' : 'billingPeriodEndValue';

    if (!inputText?.trim()) {
      // Empty input is an intentional clear.
      if ((this.formData as any)[field]) {
        this.pushUndoSnapshot();
        (this.formData as any)[field] = '';
        this.isDirty = true;
        this[prop] = null;
        // An incomplete period has no plan lookup — this clears the options.
        this.loadEnergyPlans();
        this.updateComputedValues();
        this.emitChange();
        return;
      }
      this[prop] = null;
      this.updateComputedValues();
      return;
    }

    // Non-empty text: a parseable date already landed in formData via
    // ngModelChange. Unparseable text never did — restore the last valid date
    // visibly by forcing a fresh moment reference (ngModel rewrites the input).
    const str = ((this.formData?.[field] as string) || '').substring(0, 10);
    const parsed = str ? moment(str, 'YYYY-MM-DD', true) : null;
    this[prop] = parsed?.isValid() ? parsed : null;
    this.updateComputedValues();
  }

  /** Re-derives a datepicker moment from the form's date string unless that field is being typed in. */
  private syncDateValue(field: 'billingPeriodStart' | 'billingPeriodEnd'): void {
    if (this.activeEditField === field) {
      return;
    }
    const prop = field === 'billingPeriodStart' ? 'billingPeriodStartValue' : 'billingPeriodEndValue';
    const str = (this.formData?.[field] as string) || '';
    const parsed = str ? moment(str.substring(0, 10), 'YYYY-MM-DD', true) : null;
    const next = parsed?.isValid() ? parsed : null;
    const current = this[prop];
    // Only swap the reference when the day actually changed — every reference
    // swap makes ngModel rewrite the input text.
    if ((current == null) !== (next == null) || (current && next && !current.isSame(next, 'day'))) {
      this[prop] = next;
    }
  }

  handleSupplierChange(value: string | null): void {
    this.pushUndoSnapshot();
    if (value === '__on_utility__') {
      this.formData.supplierId = undefined;
      this.formData.providerName = 'On Utility';
      this.formData.energyPlanId = undefined;
    } else if (value) {
      const supplier = this.suppliers.find(s => s.id === value);
      this.formData.supplierId = value;
      this.formData.providerName = supplier?.name || '';
    } else {
      this.formData.supplierId = undefined;
      this.formData.providerName = '';
    }
    this.buildSupplierOptions();
    // Full reload (not just the in-memory filter): a manually built bill has no
    // plans fetched yet until a supplier is picked. Also recomputes canCreatePlan.
    this.loadEnergyPlans();
    this.isDirty = true;
    this.updateComputedValues();
    this.emitChange();
  }

  handleUnitChange(unitName: string): void {
    this.pushUndoSnapshot();
    this.formData.usageUnit = unitName;
    // Stamp the platform unit id so the save persists the chosen unit (e.g. MWh)
    // instead of the persistence layer defaulting electricity back to kWh.
    this.formData.usageUnitId = this.availableUnits.find(u => u.unit === unitName)?.id;
    this.isDirty = true;
    this.emitChange();
  }

  handleCurrencyChange(field: keyof ParsedBillData, displayValue: string): void {
    if (this.pendingUndoForField !== field) {
      this.pushUndoSnapshot();
      this.pendingUndoForField = field;
    }
    this.activeEditField = field;
    const num = parseFloat(displayValue.replace(/[^0-9.\-]/g, ''));
    (this.formData as any)[field] = isNaN(num) ? 0 : num;
    if (field === 'totalAmount') {
      this.totalManuallyChanged = true;
    }
    this.isDirty = true;
    this.updateComputedValues();
    this.emitChange();
  }

  onCurrencyBlur(field: keyof ParsedBillData, inputEl?: HTMLInputElement): void {
    this.activeEditField = null;
    this.pendingUndoForField = null;
    this.updateComputedValues();
    this.resyncInputDisplay(field, inputEl);
  }

  formatCurrency(value: number): string {
    if (value == null || isNaN(value)) return '';
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /** Toggle the Supply section. Off = not on this bill: inputs collapse and the
   *  section is excluded from validation, but entered values are kept so toggling
   *  back on restores them (the editor's save path clears a disabled side). */
  setSupplyEnabled(enabled: boolean): void {
    this.pushUndoSnapshot();
    this.supplyEnabled.set(enabled);
    this.syncSectionFlags();
    this.isDirty = true;
    this.updateComputedValues();
    this.emitChange();
  }

  /** Toggle the Distribution section. See {@link setSupplyEnabled}. */
  setDistributionEnabled(enabled: boolean): void {
    this.pushUndoSnapshot();
    this.distributionEnabled.set(enabled);
    this.syncSectionFlags();
    this.isDirty = true;
    this.updateComputedValues();
    this.emitChange();
  }

  /** Budgeting mode requires both sides unless the bill is dual — force both on
   *  when per-section toggling isn't available so a hidden side can't stay off. */
  private enforceSectionAvailability(): void {
    if (!this.sectionTogglesAvailable()) {
      this.supplyEnabled.set(true);
      this.distributionEnabled.set(true);
    }
  }

  /** Mirror the section signals onto the persisted formData flags. financialsDisabled
   *  stays as the "both off" (total-only) convenience flag. */
  private syncSectionFlags(): void {
    if (!this.formData) return;
    const supplyOff = !this.supplyEnabled();
    const distOff = !this.distributionEnabled();
    this.formData.supplyDisabled = supplyOff;
    this.formData.distributionDisabled = distOff;
    this.formData.financialsDisabled = supplyOff && distOff;
  }

  /** Handle editable currency fields in basic mode — parse on keystroke, defer formatting to blur */
  handleBasicCurrencyChange(field: keyof ParsedBillData, displayValue: string): void {
    if (this.pendingUndoForField !== field) {
      this.pushUndoSnapshot();
      this.pendingUndoForField = field;
    }
    this.activeEditField = field;
    // Blank clears to null (a total-only bill); a typed number is used as-is.
    const cleaned = (displayValue ?? '').replace(/[^0-9.\-]/g, '');
    const num = parseFloat(cleaned);
    (this.formData as any)[field] = cleaned === '' || isNaN(num) ? null : num;
    this.isDirty = true;
    this.recalculateBasicTotals();
    this.emitChange();
  }

  onBasicCurrencyBlur(field: keyof ParsedBillData, inputEl?: HTMLInputElement): void {
    this.activeEditField = null;
    this.pendingUndoForField = null;
    this.recalculateBasicTotals();
    this.resyncInputDisplay(field, inputEl);
  }

  private recalculateBasicTotals(): void {
    // Don't auto-set totalAmount — let the user decide via the calculator button
    this.updateComputedValues();
  }

  /** Manually sync totalAmount to match Supply + Distribution */
  syncTotalToCalculated(): void {
    this.pushUndoSnapshot();
    // calculatedTotalValue is the legacy- and section-aware Supply + Distribution total the
    // mismatch card displays — syncing to it keeps the action WYSIWYG.
    this.formData.totalAmount = this.calculatedTotalValue;
    this.totalManuallyChanged = true;
    this.isDirty = true;
    this.updateComputedValues();
    this.emitChange();
  }

  applyDiffToSupply(): void {
    this.pushUndoSnapshot();
    this.formData.supplyCharges = Math.max(0, this.roundMoney((this.formData.supplyCharges || 0) + this.mismatchQuickFixDiff));
    this.isDirty = true;
    this.updateComputedValues();
    this.emitChange();
  }

  applyDiffToDistribution(): void {
    this.pushUndoSnapshot();
    // No 0-clamp: distribution may legitimately go negative (a credit, DEV-15235).
    this.formData.distributionCharges = this.roundMoney((this.formData.distributionCharges || 0) + this.mismatchQuickFixDiff);
    this.isDirty = true;
    this.updateComputedValues();
    this.emitChange();
  }

  /** Revert the total back to the originally extracted value */
  revertTotalToExtracted(): void {
    if (!this.originalBillData) return;
    this.pushUndoSnapshot();
    this.formData.totalAmount = this.originalBillData.totalAmount;
    this.totalManuallyChanged = false;
    this.isDirty = true;
    this.updateComputedValues();
    this.emitChange();
  }

  // ===========================================================================
  // Charge Groups (Pro Mode)
  // ===========================================================================

  updateGroup(field: keyof ParsedBillData, group: ChargeGroup): void {
    this.pushUndoSnapshot();
    (this.formData as any)[field] = group;
    this.recalculateTotals();
    this.isDirty = true;
    this.emitChange();
  }

  handleMoveLineItem(sourceField: keyof ParsedBillData, itemIndex: number, targetField: keyof ParsedBillData): void {
    this.pushUndoSnapshot();
    const sourceGroup = (this.formData[sourceField] as ChargeGroup) || { total: 0, lineItems: [] };
    const targetGroup = (this.formData[targetField] as ChargeGroup) || { total: 0, lineItems: [] };

    const itemToMove = sourceGroup.lineItems[itemIndex];
    if (!itemToMove) return;

    itemToMove.amount = this.roundMoney(itemToMove.amount || 0);

    const newSourceItems = sourceGroup.lineItems.filter((_, i) => i !== itemIndex);
    const newSourceTotal = this.roundMoney(newSourceItems.reduce((sum, i) => sum + (i.amount || 0), 0));
    const newTargetItems = [...targetGroup.lineItems, itemToMove];
    const newTargetTotal = this.roundMoney(newTargetItems.reduce((sum, i) => sum + (i.amount || 0), 0));

    (this.formData as any)[sourceField] = { total: newSourceTotal, lineItems: newSourceItems };
    (this.formData as any)[targetField] = { total: newTargetTotal, lineItems: newTargetItems };

    this.recalculateTotals();
    this.isDirty = true;
    this.emitChange();
  }

  private recalculateTotals(): void {
    const sum = (field: keyof ParsedBillData) => ((this.formData[field] as ChargeGroup)?.total || 0);
    const isGas = this.isGasMeter();

    // GRT/SUT and Misc are shared by both commodities — factor them out of the branch.
    const newSupplyCharges = this.roundMoney(
      (isGas
        ? sum('supplyContractCharges') + sum('supplyCustomerCharges') + sum('supplyGasCharges') +
          sum('supplyOffPeakCharges') + sum('supplyRateClassCharges')
        : sum('supplyContractCharges') + sum('supplyEnergyCharges') + sum('supplyCapacityCharges') +
          sum('supplyTransmissionCharges') + sum('supplyMarketCharges') + sum('supplyLineLossCharges'))
      + sum('supplyGrtSut') + sum('supplyMiscCharges')
    );
    const newSupplyTaxes = this.roundMoney(sum('supplySalesTaxes'));
    // Customer and Misc are shared by both commodities — factor them out of the branch.
    const newDistCharges = this.roundMoney(
      (isGas
        ? sum('distributionGasCharges') + sum('distributionDeliveryCharges') + sum('distributionOffPeakCharges') +
          sum('distributionBalancingCharges') + sum('distributionRateClassCharges') + sum('distributionGrtSut')
        : sum('distributionDemandCharges') + sum('distributionEnergyCharges'))
      + sum('distributionCustomerCharges') + sum('distributionMiscCharges')
    );
    const newDistTaxes = this.roundMoney(sum('distributionTaxCharges'));

    // Distribution counts when nonzero — a credit can make its buckets sum
    // negative (DEV-15235) and that's still a populated breakdown. Supply keeps
    // "> 0": it is positive-only everywhere else, so a negative supply sum stays
    // "no data" as before.
    if (newSupplyCharges > 0 || newDistCharges !== 0) {
      this.formData.supplyCharges = newSupplyCharges;
      this.formData.supplyTaxes = newSupplyTaxes;
      this.formData.distributionCharges = newDistCharges;
      this.formData.distributionTaxes = newDistTaxes;
      // A side with no buckets may still carry a legacy section total — keep
      // it in the bill total instead of dropping that side to 0. A disabled
      // side isn't saved, so it contributes nothing.
      const supplySide = this.supplyEnabled()
        ? this.resolveSectionTotal(newSupplyCharges + newSupplyTaxes, this.formData.supplyTotalCost) : 0;
      const distSide = this.distributionEnabled()
        ? this.resolveSectionTotal(newDistCharges + newDistTaxes, this.formData.distributionTotalCost, true) : 0;
      this.formData.totalAmount = this.roundMoney(supplySide + distSide);
    }

    this.updateComputedValues();
  }

  private roundMoney(value: number): number {
    return Math.round((value || 0) * 100) / 100;
  }

  /** Charges + taxes when the breakdown sums to nonzero; falls back to the
   *  persisted section total when it sums to 0 (legacy total-only sections).
   *  Distribution callers allow a negative persisted total — a legacy credit
   *  (DEV-15235) must survive instead of collapsing to 0. */
  private resolveSectionTotal(
    breakdownSum: number,
    persistedTotal: number | null | undefined,
    allowNegativeFallback = false,
  ): number {
    const computed = this.roundMoney(breakdownSum);
    const persisted = persistedTotal || 0;
    const usePersisted = computed === 0 && (allowNegativeFallback ? persisted !== 0 : persisted > 0);
    return usePersisted ? this.roundMoney(persisted) : computed;
  }

  revertFinancials(): void {
    if (!this.originalBillData) return;
    this.pushUndoSnapshot();
    this.formData.totalAmount = this.originalBillData.totalAmount;
    this.formData.supplyCharges = this.originalBillData.supplyCharges;
    this.formData.supplyTaxes = this.originalBillData.supplyTaxes;
    this.formData.supplyTotalCost = this.originalBillData.supplyTotalCost;
    this.formData.distributionCharges = this.originalBillData.distributionCharges;
    this.formData.distributionTaxes = this.originalBillData.distributionTaxes;
    this.formData.distributionTotalCost = this.originalBillData.distributionTotalCost;
    this.totalManuallyChanged = false;
    this.isDirty = false;
    // Revert restores the original financials — there is nothing left to undo,
    // so clear the stack (canUndo is reset by updateComputedValues below).
    this.undoStack = [];
    this.updateComputedValues();
    this.emitChange();
    this.commitRequested.emit();
  }

  // ===========================================================================
  // Undo
  // ===========================================================================

  private pushUndoSnapshot(): void {
    // The total flags travel with the data — undoing a manual total edit must
    // also resume auto-tracking, or the reconcile card reappears on a bill
    // that looks untouched.
    this.undoStack.push(JSON.stringify({
      formData: this.formData,
      totalManuallyChanged: this.totalManuallyChanged,
      autoTotalEligible: this.autoTotalEligible,
    }));
    if (this.undoStack.length > this.MAX_UNDO) {
      this.undoStack.shift();
    }
  }

  undo(): void {
    const snapshot = this.undoStack.pop();
    if (!snapshot) return;
    const state = JSON.parse(snapshot);
    this.formData = state.formData;
    this.totalManuallyChanged = state.totalManuallyChanged;
    this.autoTotalEligible = state.autoTotalEligible;
    this.isDirty = this.undoStack.length > 0 || JSON.stringify(this.formData) !== JSON.stringify(this.originalBillData);
    this.updateComputedValues();
    this.buildSupplierOptions();
    this.emitChange();
    this.commitRequested.emit();
  }

  // ===========================================================================
  // Pre-computed Template Values
  // ===========================================================================

  private updateComputedValues(): void {
    if (!this.formData) return;

    const fmtNum = (v: number) => (v == null || isNaN(v))
      ? '0.00' : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    // Skip reformatting the field the user is actively typing in (prevents cursor jump)
    const editing = this.activeEditField;

    // Billing-period moments for the datepicker inputs
    this.syncDateValue('billingPeriodStart');
    this.syncDateValue('billingPeriodEnd');

    // Formatted financial breakdown for basic mode
    const supplyCharges = this.formData.supplyCharges || 0;
    const supplyTaxes = this.formData.supplyTaxes || 0;
    const distCharges = this.formData.distributionCharges || 0;
    const distTaxes = this.formData.distributionTaxes || 0;

    // Blank (null) breakdown fields render empty, not "0.00", so a total-only
    // bill reads as cleared rather than zeroed.
    const fmtField = (v: number | null | undefined) => (v == null ? '' : fmtNum(v));
    if (editing !== 'supplyCharges') this.formattedSupplyCharges = fmtField(this.formData.supplyCharges);
    if (editing !== 'supplyTaxes') this.formattedSupplyTax = fmtField(this.formData.supplyTaxes);
    if (editing !== 'distributionCharges') this.formattedDistCharges = fmtField(this.formData.distributionCharges);
    if (editing !== 'distributionTaxes') this.formattedDistTax = fmtField(this.formData.distributionTaxes);

    // Keep derived totals in sync so the API always gets correct values. A
    // legacy bill may carry only a persisted section total with no charge
    // breakdown — keep showing/persisting that total instead of recomputing 0.
    const computedSupplyTotal = this.resolveSectionTotal(supplyCharges + supplyTaxes, this.formData.supplyTotalCost);
    const computedDistTotal = this.resolveSectionTotal(distCharges + distTaxes, this.formData.distributionTotalCost, true);
    this.formData.supplyTotalCost = computedSupplyTotal;
    this.formData.distributionTotalCost = computedDistTotal;
    this.formattedTotalSupply = fmtNum(computedSupplyTotal);
    this.formattedTotalDist = fmtNum(computedDistTotal);

    // Disabled sections keep their values so re-enabling restores them, but they
    // aren't saved — exclude them from the bill total and the reconcile math.
    const supplyOn = this.supplyEnabled();
    const distOn = this.distributionEnabled();
    const supplyPlusDist = this.roundMoney(
      (supplyOn ? computedSupplyTotal : 0) + (distOn ? computedDistTotal : 0)
    );

    // A bill that never had a real total auto-tracks the enabled sections until
    // the user edits the total field. With both sections off (total-only bill)
    // the total is purely user-entered, so there is nothing to auto-calculate.
    if (this.autoTotalEligible && !this.totalManuallyChanged && (supplyOn || distOn)) {
      this.formData.totalAmount = supplyPlusDist;
    }

    // Formatted total amount
    const total = this.formData.totalAmount;
    if (editing !== 'totalAmount') {
      this.formattedTotalAmount = fmtNum(total || 0);
    }

    const signedDiff = this.roundMoney((total || 0) - supplyPlusDist);
    // Nonzero (not "> 0") — a distribution credit can drag Supply + Distribution
    // negative (DEV-15235); the mismatch check still applies then. A zero sum
    // suppresses it (an empty breakdown — or, as an accepted limitation, sides
    // that exactly cancel).
    this.totalMismatch = supplyPlusDist !== 0 && Math.abs(signedDiff) > TOTAL_AMOUNT_TOLERANCE;
    this.totalAmountValue = total || 0;
    this.calculatedTotalValue = supplyPlusDist;
    this.totalDiffValue = signedDiff;
    this.mismatchQuickFixDiff = this.totalMismatch ? signedDiff : 0;
    this.refreshTotalIssue();

    this.showSupplySalesTax = this.isGasMeter()
      || supplyTaxes !== 0
      || (this.chargeGroupMap['supplySalesTaxes']?.lineItems?.length ?? 0) > 0;

    // Effective rates (charges / usage)
    const usageForRate = this.formData.usageAmount || 0;
    // Sign only when the value is still negative at display precision, so a
    // rounding sliver never prints as -$0.00000.
    const fmtRate = (v: number) => {
      const abs = Math.abs(v).toFixed(5);
      return `${v < 0 && Number(abs) !== 0 ? '-' : ''}$${abs}`;
    };
    this.formattedSupplyEffectiveRate = usageForRate > 0
      ? fmtRate(supplyCharges / usageForRate)
      : '—';
    this.formattedDistEffectiveRate = usageForRate > 0
      ? fmtRate(distCharges / usageForRate)
      : '—';
    this.formattedTotalEffectiveRate = usageForRate > 0 && total
      ? fmtRate(total / usageForRate)
      : '—';
    // The calculation behind each rate, with the same sign convention as the
    // rate itself (-$848.75, never $-848.75). Blank when there is no usage or
    // the side's charges are null: a blank must not read as $0.00.
    const fmtMoney = (v: number) => {
      const abs = fmtNum(Math.abs(v));
      return `${v < 0 && abs !== '0.00' ? '-' : ''}$${abs}`;
    };
    const usageText = usageForRate > 0
      ? `${usageForRate.toLocaleString('en-US', { maximumFractionDigits: 2 })} ${this.formData.usageUnit || ''}`.trim()
      : '';
    const rateFormula = (charges: number | null | undefined) =>
      usageForRate > 0 && charges != null ? `${fmtMoney(charges)} ÷ ${usageText} =` : '';
    this.formattedSupplyRateFormula = rateFormula(this.formData.supplyCharges);
    this.formattedDistRateFormula = rateFormula(this.formData.distributionCharges);

    // Usage amount (with commas)
    const usage = this.formData.usageAmount;
    if (editing !== 'usageAmount') {
      this.formattedUsageAmount = (usage != null && !isNaN(usage) && usage !== 0)
        ? usage.toLocaleString('en-US', { maximumFractionDigits: 2 })
        : (usage === 0 ? '0' : '');
    }

    // Demand (with commas)
    const demand = this.formData.demandKw;
    if (editing !== 'demandKw') {
      this.formattedDemand = (demand != null && !isNaN(demand))
        ? demand.toLocaleString('en-US', { maximumFractionDigits: 2 })
        : '';
    }

    // Interval usages (TOU breakdown)
    const onPeak = this.formData.onPeakUsage || 0;
    const onPeakCost = this.formData.onPeakCost || 0;
    const intPeak = this.formData.intermediateUsage || 0;
    const intPeakCost = this.formData.intermediateCost || 0;
    const offPeak = this.formData.offPeakUsage || 0;
    const offPeakCost = this.formData.offPeakCost || 0;

    const intervalFacts = {
      onPeakUsage: onPeak, onPeakCost,
      intermediateUsage: intPeak, intermediateCost: intPeakCost,
      offPeakUsage: offPeak, offPeakCost,
    };
    const hasDataNow = hasIntervalData(intervalFacts);
    this.showIntervalUsages = resolveIntervalExpanded(
      this.hasIntervalData, this.showIntervalUsages, hasDataNow,
    );
    this.hasIntervalData = hasDataNow;

    if (editing !== 'onPeakUsage') {
      this.formattedOnPeakUsage = (onPeak != null && !isNaN(onPeak) && onPeak !== 0)
        ? onPeak.toLocaleString('en-US', { maximumFractionDigits: 2 }) : '';
    }
    if (editing !== 'onPeakCost') this.formattedOnPeakCost = fmtNum(onPeakCost);
    this.formattedOnPeakRate = onPeak > 0 ? (onPeakCost / onPeak).toFixed(5) : '—';

    if (editing !== 'intermediateUsage') {
      this.formattedIntPeakUsage = (intPeak != null && !isNaN(intPeak) && intPeak !== 0)
        ? intPeak.toLocaleString('en-US', { maximumFractionDigits: 2 }) : '';
    }
    if (editing !== 'intermediateCost') this.formattedIntPeakCost = fmtNum(intPeakCost);
    this.formattedIntPeakRate = intPeak > 0 ? (intPeakCost / intPeak).toFixed(5) : '—';

    if (editing !== 'offPeakUsage') {
      this.formattedOffPeakUsage = (offPeak != null && !isNaN(offPeak) && offPeak !== 0)
        ? offPeak.toLocaleString('en-US', { maximumFractionDigits: 2 }) : '';
    }
    if (editing !== 'offPeakCost') this.formattedOffPeakCost = fmtNum(offPeakCost);
    this.formattedOffPeakRate = offPeak > 0 ? (offPeakCost / offPeak).toFixed(5) : '—';

    // Interval total row + usage reconciliation check.
    // Suppress mismatch when headline Usage Amount is missing — the required-field
    // validator will surface that, and saying "doesn't match Usage Amount (0)" misleads.
    const usageAmountRaw = this.formData.usageAmount;
    const hasUsageAmount = usageAmountRaw != null && !isNaN(usageAmountRaw);
    const intervalUsageAmount = hasUsageAmount ? usageAmountRaw : 0;
    const intervalTotals = computeIntervalTotals(intervalFacts, intervalUsageAmount);
    this.formattedIntervalTotalUsage = intervalTotals.totalUsage.toLocaleString('en-US', {
      maximumFractionDigits: 2,
    });
    this.formattedIntervalTotalCost = fmtNum(intervalTotals.totalCost);
    this.formattedIntervalBlendedRate = intervalTotals.totalUsage > 0
      ? '$' + intervalTotals.blendedRate.toFixed(5)
      : '—';
    this.intervalUsageMismatch = hasUsageAmount && intervalTotals.usageMismatch;
    this.intervalUsageMismatchTooltip = this.intervalUsageMismatch
      ? `Interval usage (${this.formattedIntervalTotalUsage}) doesn't match the ` +
        `Usage Amount (${intervalUsageAmount.toLocaleString('en-US', { maximumFractionDigits: 2 })}) above.`
      : '';

    // Per-field confidence data
    const fields = [
      'providerName', 'utilityName', 'utilityAccountNumber', 'utilityAccountNumber2',
      'billingPeriodStart', 'billingPeriodEnd', 'usageAmount', 'usageUnit', 'totalAmount',
      'demandKw'
    ];
    const fc = this.formData.fieldConfidence as any || {};
    const data: Record<string, { percent: string; good: boolean; bad: boolean; ugly: boolean }> = {};
    if (this.hasAiConfidence) {
      for (const f of fields) {
        const c = fc[f] || 0;
        data[f] = {
          percent: (c * 100).toFixed(0),
          good: c > 0.75,
          bad: c > 0.5 && c <= 0.75,
          ugly: c <= 0.5
        };
      }
      // The two dates share one range field now — surface a combined dot driven
      // by the lower of the two so a weak extraction on either date still shows.
      const periodC = Math.min(fc['billingPeriodStart'] || 0, fc['billingPeriodEnd'] || 0);
      data['billingPeriod'] = {
        percent: (periodC * 100).toFixed(0),
        good: periodC > 0.75,
        bad: periodC > 0.5 && periodC <= 0.75,
        ugly: periodC <= 0.5
      };
    }
    this.confidenceData = data;

    // Supplier dropdown value
    this.supplierSelectValue = this.formData.supplierId
      || (this.formData.providerName === 'On Utility' ? '__on_utility__' : null);

    // Pre-computed boolean state (replaces getters)
    this.canUndo.set(this.undoStack.length > 0);
    this.isSupplierSelected = !!this.supplierSelectValue && this.supplierSelectValue !== '__on_utility__';
    this.isEnergyPlanRequired = this.isSupplierSelected;
    this.isEnergyPlanDisabled = this.supplierSelectValue === '__on_utility__';
    this.isFormValid.set(!!this.formData && (!this.isEnergyPlanRequired || !!this.formData.energyPlanId));

    // Pre-computed formatted original total for revert button title
    this.formattedOriginalTotal = this.formatCurrency(this.originalBillData?.totalAmount || 0);

    // Pre-computed charge group map for template
    const chargeFields = [
      'supplyContractCharges', 'supplyEnergyCharges', 'supplyCapacityCharges',
      'supplyTransmissionCharges', 'supplyMarketCharges', 'supplyLineLossCharges',
      'supplyGrtSut', 'supplyMiscCharges', 'supplySalesTaxes',
      'distributionDemandCharges', 'distributionEnergyCharges',
      'distributionCustomerCharges', 'distributionMiscCharges', 'distributionTaxCharges',
      // Gas-specific buckets
      'supplyCustomerCharges', 'supplyGasCharges', 'supplyOffPeakCharges', 'supplyRateClassCharges',
      'distributionGasCharges', 'distributionDeliveryCharges', 'distributionOffPeakCharges',
      'distributionBalancingCharges', 'distributionRateClassCharges', 'distributionGrtSut'
    ];
    const groupMap: Record<string, ChargeGroup> = {};
    for (const f of chargeFields) {
      groupMap[f] = (this.formData[f as keyof ParsedBillData] as ChargeGroup) || { total: 0, lineItems: [] };
    }
    this.chargeGroupMap = groupMap;
  }

  /**
   * Rebuilds the total-issue messaging from the latest mismatch + validation state.
   * `isInvalid` mirrors `fieldErrors()['totalAmount']` so the issue icon and the
   * sibling `.field-error` border always agree — do not source `isInvalid` from
   * a separate validation pass.
   */
  private refreshTotalIssue(): void {
    this.totalIssue = buildTotalIssueMessaging({
      isMismatch: this.totalMismatch,
      isInvalid: !!this.fieldErrors()['totalAmount'],
      totalAmount: this.totalAmountValue,
      calculatedTotal: this.calculatedTotalValue,
      diff: this.totalDiffValue,
    });
  }

  // ===========================================================================
  // Helpers
  // ===========================================================================

  private ensureConfidenceDefaults(): void {
    const defaultConfidence: Record<string, number> = {
      providerName: 0.65, legalEntity: 0.75, utilityAccountNumber: 0.70,
      utilityAccountNumber2: 0.70, serviceAddressStreet1: 0.60, serviceAddressStreet2: 0.70,
      serviceAddressCity: 0.70, serviceAddressState: 0.80, serviceAddressZip: 0.80,
      billingPeriodStart: 0.95, billingPeriodEnd: 0.95, totalAmount: 0.55,
      currency: 0.99, supplyCharges: 0.85, supplyTaxes: 0.85,
      distributionCharges: 0.85, distributionTaxes: 0.85, usageAmount: 0.85, usageUnit: 0.80
    };
    this.formData.fieldConfidence = { ...defaultConfidence, ...(this.formData.fieldConfidence || {}) } as any;
  }
}
